import { Navigation } from '@/features/agents/components/navigation';
import {
  DefaultValues,
  Step,
} from '@/features/agents/components/capabilities-studio-widget';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useGetAgent } from '@/features/agents/hooks/use-get-agent';
import { useRefineAgent } from '@/features/agents/hooks/use-refine-agent';
import { useQueryClient } from '@tanstack/react-query';
import { useEmitter } from '@/hooks/use-emitter';
import { useTabId } from '@/hooks/use-tab-id';
import { useScreenId } from '@/hooks/use-screen-id';
import { useEffect, useState } from 'react';
import { useListener } from '@/hooks/use-listener';
import { Feed } from '@/features/agents/components/feed';
import { useRevertAgent } from '@/features/agents/hooks/use-revert-agent';
import { WidgetID } from '@/widget-store';
import {
  LinkIcon,
  PhotoIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { PromptBuilder } from '@/components/prompt-builder';
import { JsonEditor } from '@/components/json-editor';
import { DragDrop } from '@/components/drag-drop';
import { ObjectParams } from '@/hooks/use-relation-insert';
import { useObjectJson } from '@/hooks/use-object-json';
import { RevisionHistory } from '@/features/agents/components/revision-history';

export type TuneFormInputs = {
  tune?: string;
  title?: string;
};

interface TuneFormProps {
  id: WidgetID;
  steps: Step[];
  defaultValues: DefaultValues['tune'];
  onChange: (data: TuneFormInputs, step: 'tune') => void;
  branch: string;
  agent: unknown;
  isLoading: boolean;
}

export function TuneForm({
  id,
  branch,
  defaultValues,
  steps,
  onChange,
  agent: agentData,
  isLoading: isAgentLoading,
}: TuneFormProps) {
  const tabId = useTabId();
  const screenId = useScreenId();
  const idPrefix = `${tabId}/${screenId}`;

  const { uuid } = useParams();
  // const { data: agentData } = useGetAgent(uuid!);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<TuneFormInputs>({
    values: {
      ...(defaultValues || {}),
    },
  });
  const [jsonSource, setJsonSource] = useState<ObjectParams | null>(null);
  const { data: jsonSourceData } = useObjectJson(
    jsonSource?.uuid || '',
    jsonSource?.container || '',
    {
      refetchInterval: 0,
    }
  );

  const queryClient = useQueryClient();
  const emit = useEmitter({
    id,
    listenersPrefix: idPrefix,
  });
  const refineAgentMutation = useRefineAgent();
  const [isTuneDisabled, setIsTuneDisabled] = useState(false);
  const revertAgentMutation = useRevertAgent();
  const [feedback, setFeedback] = useState<string>();

  useListener({
    id,
    listenerFn: () => {
      setIsTuneDisabled(true);
    },
    type: 'source-code/pending',
  });

  useListener({
    id,
    listenerFn: () => {
      setIsTuneDisabled(false);
      queryClient.invalidateQueries({
        queryKey: ['agent', uuid],
        refetchType: 'all',
      });
    },
    type: 'source-code/success',
  });

  useEffect(() => {
    if (!agentData?.code || isTuneDisabled) {
      return;
    }

    emit({
      type: 'source-code/success',
      data: {
        sourceCode: {
          content: agentData.code,
          language: agentData.codeLanguage,
          previousContent: agentData.codePrevious,
        },
        agentUuid: uuid!,
      },
    });
  }, [agentData, isTuneDisabled]);

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  function onTune(data: TuneFormInputs) {
    emit({ type: 'source-code/loading' });

    refineAgentMutation.mutate(
      {
        branch,
        agentUuid: uuid!,
        title: data.title,
        corrections: data.tune,
      },
      {
        onSuccess: (data) => {
          queryClient.invalidateQueries({ queryKey: ['agent', uuid] });
          queryClient.invalidateQueries({
            queryKey: ['revision-history', uuid],
          });

          emit({
            type: 'source-code/success',
            data: {
              sourceCode: {
                content: data.code,
                language: data.codeLanguage,
                previousContent: data.codePrevious,
              },
              agentUuid: uuid!,
            },
          });

          setFeedback(data.data.feedback);
        },
        onError: () => {
          emit({
            type: 'source-code/success',
            data: {
              sourceCode: {
                content: '',
                language: '',
                previousContent: '',
              },
              agentUuid: uuid!,
            },
          });
        },
      }
    );
  }

  function handleRevertClick(revision: number) {
    revertAgentMutation.mutate(
      {
        uuid: uuid!,
        revision,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['agent', uuid] });
        },
      }
    );
  }

  const isLoading =
    refineAgentMutation.isLoading || revertAgentMutation.isLoading;

  function handleJsonSourceDrop(data: Record<string, unknown>) {
    if (data.type !== 'object/insert') {
      return;
    }

    const value = data.value as ObjectParams[];
    setJsonSource(value[0]);
  }

  return (
    <>
      <Navigation loadingStep={isLoading ? 'tune' : undefined} steps={steps} />
      {isAgentLoading ? (
        <div>Loading...</div>
      ) : (
        <form
          onSubmit={handleSubmit(onTune)}
          className="p-7 grid grid-cols-2 gap-7"
        >
          <div className="col-span-2">
            <label
              htmlFor="agentTitle"
              className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100 sr-only"
            >
              Agent Title
            </label>
            <div className="relative flex items-center gap-4">
              {agentData?.imageUrl ? (
                <img
                  src={agentData?.imageUrl}
                  alt=""
                  className="w-10 h-10 rounded-full flex-none"
                />
              ) : (
                <div className="w-10 h-10 rounded-full flex-none bg-slate-50 dark:bg-gray-800 grid place-items-center">
                  <PhotoIcon className="w-5 h-5 stroke-slate-500 dark:stroke-gray-400" />
                </div>
              )}
              <input
                type="text"
                id="title"
                className="block bg-slate-50 dark:bg-gray-800 w-full rounded-md border-0 py-2.5 px-3 text-gray-900 dark:text-gray-100 shadow-sm placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-2xl sm:leading-6"
                {...register('title', {
                  onChange: (e) => {
                    onChange(
                      {
                        ...defaultValues,
                        title: e.target.value,
                      },
                      'tune'
                    );
                  },
                })}
              />
              <span className="text-sm text-gray-500 dark:text-gray-400 absolute top-1/2 -translate-y-1/2 right-4">
                Rev {agentData?.revision}
              </span>
            </div>
            {errors.title && (
              <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                Title is required.
              </p>
            )}
          </div>
          <div>
            <div>
              <label
                htmlFor="sourceExample"
                className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
              >
                Test Input
              </label>
              <div className="space-y-1 mt-2">
                <DragDrop
                  allowedTypes={['object/insert']}
                  onDrop={handleJsonSourceDrop}
                  className="relative flex items-center space-x-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-purple px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 dark:focus-within:ring-indigo-400 focus-within:ring-offset-2 hover:border-gray-400 dark:hover:border-gray-500 border-dashed justify-center text-center h-5 mb-1 bg-white dark:bg-gray-800"
                  droppingClassName="relative flex items-center space-x-3 rounded-lg border border-gray-300 dark:border-gray-600 px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 dark:focus-within:ring-indigo-400 focus-within:ring-offset-2 hover:border-gray-800 dark:hover:border-gray-500 border-dashed bg-purple-50 dark:bg-purple-900/50 flex justify-center items-center h-5"
                >
                  {({ isDropping }) =>
                    isDropping ? (
                      <PlusIcon className="h-4 w-4 text-gray-900 dark:text-gray-100" />
                    ) : (
                      <PlusIcon className="h-4 w-4 text-gray-900 dark:text-gray-100" />
                    )
                  }
                </DragDrop>
                {jsonSource?.uuid && jsonSource?.container ? (
                  <div className="relative flex items-center gap-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-2 pr-3 shadow-sm text-xs">
                    <div className="relative w-10 h-10 bg-purple-50 dark:bg-purple-900/50 rounded-full flex justify-center items-center text-center">
                      <LinkIcon className="h-5 w-5 text-gray-900 dark:text-gray-100" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {jsonSource.container}
                      </p>
                      <p className="truncate text-gray-500 dark:text-gray-400">
                        {jsonSource.uuid}
                      </p>
                    </div>
                    <button
                      onClick={() => setJsonSource(null)}
                      className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
            <JsonEditor data={JSON.parse(jsonSourceData?.data || '{}')} />
          </div>
          <div className="col-start-2 flex flex-col">
            <label
              htmlFor="tune"
              className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
            >
              Tune
            </label>
            <div className="mt-2 h-full">
              <PromptBuilder
                content={JSON.parse(defaultValues?.tune || '{}')}
                onChange={(value) => {
                  onChange(
                    {
                      ...defaultValues,
                      tune: value,
                    },
                    'tune'
                  );
                  setValue('tune', value);
                }}
              />
              <input type="hidden" {...register('tune', { required: true })} />
              {errors.tune && (
                <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                  Please enter your tuning need first.
                </p>
              )}
            </div>
          </div>
          <div className="col-start-1">
            <span className="text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
              Feedback
            </span>
            <PromptBuilder className="mt-2 no-shadow" key={agentData?.revision} content={feedback} readOnly />
          </div>
          <div className="col-start-2">
            <RevisionHistory branch={branch} />
          </div>
          <div className="col-span-2 flex justify-end">
            <button
              className="rounded-md bg-indigo-600 dark:bg-indigo-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 dark:hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:focus-visible:outline-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isTuneDisabled}
            >
              Tune
            </button>
          </div>
        </form>
      )}
    </>
  );
}
